import React, { useEffect, useState } from 'react';
import { View, Text, Button, StyleSheet, Platform, Alert } from 'react-native';
import { Html5Qrcode, Html5QrcodeResult } from 'html5-qrcode'; // Removed Html5QrcodeError as it's not exported/used
import { signInAnonymously } from 'firebase/auth';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { auth, firestore } from '../config/firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Function to create user document (copied from LoginScreen logic for reuse)
const createUserDocument = async (userId: string, groupId: string) => {
  try {
    const userRef = doc(firestore, 'users', userId);
    await setDoc(userRef, {
      groupId: groupId,
      role: 'user',
      createdAt: new Date().toISOString(),
    });
    console.log('User document created successfully for', userId);
    return true;
  } catch (error) {
    console.error('Error creating user document:', error);
    return false;
  }
};

const QrScannerScreen = ({ navigation }: any) => {
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const qrReaderElementId = "qr-reader-element";
  let html5QrCode: Html5Qrcode | null = null;

  useEffect(() => {
    if (Platform.OS !== 'web') {
      Alert.alert("Unsupported Platform", "QR Scanner is only available on the web.");
      navigation.goBack();
      return;
    }

    // Dynamically create the div if it doesn't exist (more robust)
    let qrElement = document.getElementById(qrReaderElementId);
    if (!qrElement) {
        qrElement = document.createElement('div');
        qrElement.id = qrReaderElementId;
        // Append it somewhere sensible, e.g., a container div in your return()
        // For now, we assume it will be in the return statement.
    }

    html5QrCode = new Html5Qrcode(qrReaderElementId);
    let scannerRunning = true;

    const qrCodeSuccessCallback = async (decodedText: string, decodedResult: Html5QrcodeResult) => {
      if (!scannerRunning || isLoading) return; // Prevent multiple triggers
      
      console.log(`Scan successful: ${decodedText}`, decodedResult);
      setScanResult(decodedText);
      setIsLoading(true);
      setScanError(null);
      scannerRunning = false; // Stop scanning visually first

      try {
        // Stop the camera feed
        if (html5QrCode) {
            await html5QrCode.stop();
            console.log("QR Code scanning stopped.");
        }

        // --- Login Logic (adapted from LoginScreen) ---
        console.log('Authenticating anonymously...');
        const userCredential = await signInAnonymously(auth);
        if (!userCredential?.user?.uid) {
            throw new Error('authentication_failed');
        }
        const userId = userCredential.user.uid;
        console.log('Anonymous authentication successful, UID:', userId);

        console.log('Verifying QR code in Firestore...');
        const groupsRef = collection(firestore, 'groups');
        const q = query(groupsRef, where('qrCode', '==', decodedText)); // Use scanned text
        const groupSnapshot = await getDocs(q);

        if (groupSnapshot.empty) {
            console.error('Invalid QR code: No matching group found.');
            // --- Clean up pending keys on invalid code --- 
            await AsyncStorage.removeItem('pendingGroupCode'); 
            await AsyncStorage.removeItem('pendingGroupName');
            throw new Error('invalid_code');
        }

        const groupDoc = groupSnapshot.docs[0];
        const groupId = groupDoc.id;
        const groupName = groupDoc.data().name || 'Unknown Group';
        console.log(`Group found: ID=${groupId}, Name=${groupName}`);

        // Create user document
        const userCreated = await createUserDocument(userId, groupId);
        if (!userCreated) {
            throw new Error('user_creation_failed');
        }

        // Store session data
        await AsyncStorage.setItem('currentGroupId', groupId);
        await AsyncStorage.setItem('currentQrCode', decodedText); // Store the scanned code
        await AsyncStorage.setItem('isLoggedIn', 'true');
        
        // --- Clean up pending keys after successful login ---
        await AsyncStorage.removeItem('pendingGroupCode'); 
        await AsyncStorage.removeItem('pendingGroupName');
        console.log('Cleared pending keys after successful QR scan login.');
        // --- End Clean up ---

        console.log('Login successful, navigating to Home...');

        Alert.alert("Login Succesvol", `Ingelogd bij groep: ${groupName}`);
        navigation.replace('Home'); // Navigate to home screen on success
        // --- End Login Logic ---

      } catch (error: any) {
        console.error('Error processing QR code login:', error);
        let errorMessage = 'Login met QR code mislukt.';
        if (error.message === 'invalid_code') {
          errorMessage = 'Ongeldige QR code. Geen overeenkomende groep gevonden.';
        } else if (error.message === 'authentication_failed') {
          errorMessage = 'Authenticatie mislukt. Probeer het opnieuw.';
        } else if (error.message === 'user_creation_failed') {
          errorMessage = 'Kon gebruikersprofiel niet aanmaken. Probeer het opnieuw.';
        }
        setScanError(errorMessage);
        Alert.alert('Login Fout', errorMessage);
        // Optionally restart scanner or allow user to retry
        scannerRunning = true; // Allow scanning again after error
        startScanner(); // Attempt to restart the scanner
      } finally {
          setIsLoading(false);
      }
    };

    const qrCodeErrorCallback = (errorMessage: string, error: any) => {
      // Only log significant errors, not 'QR code parse error'
      if (!errorMessage.includes('finding QR code')) {
        // console.warn(`QR Code scan error: ${errorMessage}`, error);
      }
    };

    const startScanner = () => {
      if (!html5QrCode || !scannerRunning) return;
      html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        qrCodeSuccessCallback,
        qrCodeErrorCallback
      ).catch(err => {
        let errorMsg = "Kon de camera niet starten.";
        let alertMsg = errorMsg;
        console.error("Unable to start scanning.", err); // Log the full error

        // Check for permission errors (common messages)
        const errorString = String(err).toLowerCase();
        if (errorString.includes('permission denied') || 
            errorString.includes('notallowederror') ||
            errorString.includes('unable to query supported devices')) { // Another common error
          errorMsg = "Camera toestemming geweigerd.";
          alertMsg = "Je moet de app toegang geven tot de camera om QR codes te scannen. Controleer de browser instellingen.";
          setScanError(alertMsg); // Update state with user-friendly message
        } else {
          // Generic error message
          alertMsg = `${errorMsg} (${err.message || err})`;
          setScanError(alertMsg); // Update state with generic error
        }
        
        Alert.alert("Scanner Fout", alertMsg); 
        // Consider adding a button here to allow user to retry or go back
        // navigation.goBack(); // Don't navigate back automatically on error
      });
      console.log("QR Code scanning started.");
    };

    startScanner();

    // Cleanup function
    return () => {
      scannerRunning = false;
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().then(() => {
          console.log("QR Code scanning stopped on unmount.");
        }).catch(err => {
          console.error("Error stopping scanner on unmount:", err);
        });
      }
    };
  }, [navigation]); // Rerun effect if navigation changes (though unlikely needed here)

  if (Platform.OS !== 'web') {
    return <View><Text>QR Scanner is only available on web.</Text></View>;
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Scan Groep QR Code</Text>
      {/* This div is crucial and must exist in the DOM for the scanner */} 
      <View style={styles.scannerContainer}>
        <div id={qrReaderElementId} style={styles.scannerView}></div>
      </View>
      {isLoading && <Text style={styles.loadingText}>QR Code verwerken...</Text>}
      {scanError && <Text style={styles.errorText}>Error: {scanError}</Text>}
      {/* Optional: Show last scan result for debugging 
      {scanResult && !isLoading && !scanError && (
        <Text style={styles.resultText}>Laatste scan: {scanResult}</Text>
      )} */} 
      <Button title="Annuleren" onPress={() => navigation.goBack()} disabled={isLoading} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start', // Align top
    padding: 20,
    paddingTop: 40, // Add padding top
    backgroundColor: 'white',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  scannerContainer: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: 1, // Make it square-ish or adjust as needed
    overflow: 'hidden', // Important for contained view
    borderWidth: 1,
    borderColor: 'lightgrey',
    borderRadius: 8,
    marginBottom: 20,
    position: 'relative', // Needed for potential overlays
  },
  scannerView: {
    // The div itself might not need explicit styling if container handles size
    width: '100%', 
    height: '100%', 
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: 'orange',
  },
  errorText: {
    marginTop: 15,
    fontSize: 16,
    color: 'red',
    textAlign: 'center',
  },
  resultText: {
    marginTop: 15,
    fontSize: 14,
    color: 'green',
  },
});

export default QrScannerScreen; 